<template>
  <div
    ref="rootEl"
    class="qtl-shell"
    :class="[
      { 'qtl-shell--standalone': !isSector && !isInDialog },
      `qtl-shell--${headerMode}`,
    ]"
  >
    <!--
      Панели сверху и снизу (счётчики, страницы, переключатели) живут в родителе
      QueryTable.vue. Тут только сама таблица.

      Таблица одна, ширины колонок считает сам браузер по содержимому, как было
      раньше. А вот как ведёт себя шапка и прокрутка, задаёт headerMode: шапка
      прилипает сверху, уезжает со скроллом, или таблица вообще без своей
      прокрутки лежит в общем контейнере (смотри QTLiteBody).
    -->
    <div ref="tablesEl" class="qtl-tables">
      <QTLiteBody
        ref="bodyRef"
        :rows="rows"
        :columns="columns"
        :measure-sample-size="measureSampleSize"
        :virtualized="virtualized"
        :virtual-rows="virtualRows"
        :total-size="totalSize"
        :scroll-margin="scrollMargin"
        :measure-element="measureRowElement"
        :header-mode="headerMode"
        :formula-results="formulaResults"
        :user-widths="userWidths"
        :frozen-col-widths="frozenColWidths"
        :drag-live-widths="dragLiveWidths"
        :widths-locked="widthsLocked"
        :locked-width-px="lockedTableWidthPx"
        :effective-multiplier="effectiveMultiplier"
        :row-height-align-stamp="rowHeightAlignStamp"
        :content-mode="bodyContentMode"
        :src-element="srcElement"
        :WA="WA"
        :is-row-checked="entityChecked"
        :row-number-base="displayedRowNumberBase"
        :scroll-locked="showRefreshStrip"
        :loading="loading"
        :empty-message="emptyMessage"
        :header-checkbox="headerCheckbox"
        :multiedit-allowed="multieditAllowed"
        :order-by-pinned="!!props.paginationModel?.OrderByPinned"
        :show-global-pinned-sort="!props.isSector && !props.hidePanel"
        @headerCheckboxChange="onHeaderCheckboxChange"
        @sort="onSort"
        @multieditClick="onMultieditClick"
        @togglePinnedOrder="onTogglePinnedOrder"
        @toggleCheckbox="onRowToggleCheckbox"
        @changeHoverRecord="onRecordHover"
        @changeHeights="onChangeHeights"
        @refreshNeeded="onRefreshNeeded"
        @updatePerson="onUpdatePerson"
        @updateSelection="onCellUpdateSelection"
        @checkBoxClick="onCheckboxClick"
        @sourceItemsRequested="onSourceItemsRequested"
      />

      <!--
        Тонкая бегущая полоска загрузки. Рисуем её ровно по нижней границе реальной
        шапки: отступ сверху = верх таблицы внутри контейнера + высота шапки. Над
        таблицей может лежать блок формул переменной высоты (он внутри скролла), а в
        режиме scroll шапка уезжает, поэтому отсчёт динамический, а не от верха скролла.
      -->
      <div
        v-if="showRefreshStrip"
        class="qtl-refresh-strip"
        :style="{ top: (tableTopOffset + headerHeight) + 'px', right: stripRight + 'px' }"
        aria-hidden="true"
      >
        <div class="qtl-refresh-strip__bar" />
      </div>

      <!--
        Поверх таблицы лежат тонкие прозрачные полоски, за них тянут, чтобы менять
        ширину колонок, плюс линия подсказки пока тащим. На время загрузки прячем.
        Вертикальный отсчёт начинаем от верхней границы таблицы (top-offset), чтобы
        хваталки и курсор col-resize не залезали в зону формул над таблицей.
      -->
      <QTLiteResizeOverlay
        v-if="!showRefreshStrip"
        :columns="columns"
        :table-el="bodyTableEl"
        :scroll-el="bodyScrollEl"
        :top-offset="tableTopOffset"
        @resizeStart="onResizeStart"
        @resize="onResize"
        @resizeEnd="onResizeEnd"
      />
    </div>

    <!-- Один общий диалог источников проекции на всю таблицу: ячейки SourceItems
         только эмитят запрос, диалог открывается здесь. -->
    <SourceItemsDialog ref="sourceDialogRef" />
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
  type Ref,
} from "vue";

import { useVirtualizer } from "@tanstack/vue-virtual";

import QTLiteBody from "./parts/QTLiteBody.vue";
import QTLiteResizeOverlay from "./parts/QTLiteResizeOverlay.vue";
import SourceItemsDialog from "./parts/SourceItemsDialog.vue";

import { useTableModel } from "./composables/useTableModel";
import { widthMultiplierOf } from "./composables/useColumnWidths";
import { useMultiEditState } from "./composables/useMultiEditState";
import {
  beginColumnResize,
  endColumnResize,
  beginWindowResize,
  endWindowResize,
  isWindowResizing,
  isColumnDragLayoutAuto,
  resizeSettleCounter,
} from "./composables/resizeState";
import { liteHeaderModeFlag } from "./featureFlag";
import { auxStore } from "@/store/auxillary";
import { queryStore } from "@/store/querytable";
import { generalStore } from "@/store/general";

import type {
  AnalyticsResultTableExtended,
} from "@/models/interfaces/entity-settings/querytable/querytable";
import type { LiteColumn } from "./types";
import { i18n } from "@/i18n";

const t = (key: string) => i18n.global.t(key);

/*
 Входные параметры и события, такие же, как у обычной таблицы.
*/

const props = withDefaults(defineProps<{
  modelValue: AnalyticsResultTableExtended | undefined;
  paginationModel: any;
  srcElement: any;
  selectedEverything?: string;
  selectedEntities?: number[];
  unselectedEntities?: number[];
  isSector?: boolean;
  isInDialog?: boolean;
  hidePanel?: boolean;
}>(), {
  selectedEverything: "none",
  selectedEntities: () => [],
  unselectedEntities: () => [],
  isSector: false,
  isInDialog: false,
  hidePanel: false,
});

const emit = defineEmits<{
  (e: "update:modelValue", v: any): void;
  (e: "update:paginationModel", v: any): void;
  (e: "update:selectedEverything", v: string): void;
  (e: "update:selectedEntities", v: number[]): void;
  (e: "update:unselectedEntities", v: number[]): void;
  (e: "selectionChangeManual"): void;
  (e: "refreshPage", v: any): void;
  (e: "reloadTable"): void;
  (e: "refreshNeeded"): void;
  /** нажали карандашик в первой колонке, надо открыть окно массовой правки */
  (e: "multieditClick", ev: MouseEvent): void;
  /** переключили общую сортировку по закреплённым, значок в первой колонке */
  (e: "togglePinnedOrder"): void;
  (e: "updatePerson", payload: { ev: MouseEvent; entityId: number }): void;
}>();

/*
 Хранилища.
*/

const aux = auxStore();
const qstore = queryStore();
const gstore = generalStore();

const loading = computed<boolean>(() => qstore.isLoading || aux.isTableLoading);

/*
 Ссылки на элементы.
*/

const rootEl = ref<HTMLElement | null>(null);
const tablesEl = ref<HTMLElement | null>(null);
const bodyRef = ref<InstanceType<typeof QTLiteBody> | null>(null);

const bodyScrollEl = computed<HTMLElement | null>(() => bodyRef.value?.bodyScrollEl ?? null);
const bodyTableEl = computed<HTMLTableElement | null>(() => bodyRef.value?.tableEl ?? null);

/* Результаты формул лежат на самой модели таблицы. В lite показываем их внутри
   скролла над таблицей (см. QTLiteBody), чтобы они уезжали вместе с шапкой. */
const formulaResults = computed(() => props.modelValue?.FormulaResults ?? []);

/*
 Права на правку, как и было в обычной таблице.
*/

const WA = computed<boolean>(() => !!gstore.accessRights?.WriteAccess);
const multieditAllowed = computed<boolean>(() => (
  (gstore.selectedItem?.DestType !== 6) &&
  Number(props.paginationModel?.total_count || 0) > 0 &&
  !(
    multi.headerCheckbox.value === "none" &&
    (!props.selectedEntities || !props.selectedEntities.length)
  )
));

/* Как ведёт себя шапка и прокрутка: прилипает сверху, уезжает со скроллом, или
   таблица без своей прокрутки лежит в общем контейнере. Берётся из общей настройки.
   В секторе своей прокрутки у таблицы быть не должно: она лежит в скролл-контейнере
   карточки сектора (q-card-section.scroll), и собственный скролл тела перехватывал бы
   колесо. Поэтому в секторе всегда режим page — таблица растёт по содержимому, а
   прокручивается вместе с карточкой сектора. */
const headerMode = computed(() => (props.isSector ? "page" : liteHeaderModeFlag.value));

/*
 Колонки и строки таблицы.
*/

const tableModel = useTableModel({
  source: () => props.modelValue,
  hasWriteAccess: () => WA.value,
  destType: () => gstore.selectedItem?.DestType || 0,
});

const columns = tableModel.columns;
const rows = tableModel.rows;

const columnsFingerprint = computed<string>(() =>
  columns.value.map((c) => c.id + ":" + (c.text || "")).join("|"),
);
const columnIds = computed<string[]>(() => columns.value.map((c) => c.id));

// Ключ структуры шапки: набор + порядок id колонок. По нему решаем, сохранять ли
// ширины при обновлении. Текст заголовка и ширина на ключ не влияют. id — та же
// идентичность, по которой хранятся userWidths, поэтому сравнение согласовано.
const columnsStructureKey = computed<string>(() => columnIds.value.join("|"));

/*
 Ширина таблицы, которую задаёт переключатель сверху.
 В окне и в секторе всегда по ширине окна. В остальных случаях берём из настройки
 (один экран, два экрана и так далее). Это число задаёт минимальную ширину таблицы,
 а браузер уже сам раскидывает это место между колонками, как было раньше.
*/

const containerWidth = ref<number>(0);
const widthMultiplier = computed<number>(() => widthMultiplierOf(props.modelValue?.TableWidth));
const effectiveMultiplier = computed<number>(() => (props.isSector || props.isInDialog ? 1 : widthMultiplier.value));

/* Ресайз окна/контейнера считаем завершённым, когда ширина не менялась столько мс.
   До этого держим isWindowResizing, чтобы строки не пересчитывали высоты параметров
   на каждом кадре (при сотнях строк это тысячи замеров). */
const WINDOW_RESIZE_SETTLE_MS = 160;
let windowResizeSettleTimer: number | null = null;
function scheduleWindowResizeSettle() {
  if (windowResizeSettleTimer !== null) clearTimeout(windowResizeSettleTimer);
  windowResizeSettleTimer = window.setTimeout(() => {
    windowResizeSettleTimer = null;
    // ресайз замер: один раз подгоняем зафиксированные ширины под новый размер
    // контейнера (во время перетаскивания их не трогали, чтобы браузер не перекладывал
    // 800 строк каждый кадр), затем выравниваем высоты. endWindowResize увеличивает
    // resizeSettleCounter, а он входит в rowHeightAlignStamp — строки один раз
    // пересчитают высоты параметров.
    readaptWidthsAfterResize();
    endWindowResize();
  }, WINDOW_RESIZE_SETTLE_MS);
}

let containerRO: ResizeObserver | null = null;
function setupContainerObserver() {
  if (containerRO || !rootEl.value) return;
  containerRO = new ResizeObserver((entries) => {
    const oldW = containerWidth.value;
    let newW = oldW;
    let widthChanged = false;
    for (const entry of entries) {
      const w = entry.contentRect.width;
      if (Math.abs(w - newW) > 1) { newW = w; widthChanged = true; }
    }
    if (widthChanged) containerWidth.value = newW;
    measureLayout();
    // при реальном изменении ширины контейнера (окно, левая панель, девтулс) на время
    // ресайза глушим пересчёт высот параметров по всем строкам. Ширины во время
    // перетаскивания НЕ трогаем — таблица держит фиксированную ширину, браузер не
    // перекладывает 800 строк каждый кадр. Один пересчёт ширин и высот случится после
    // того, как ресайз замер (scheduleWindowResizeSettle → readaptWidthsAfterResize).
    if (widthChanged) {
      beginWindowResize();
      scheduleWindowResizeSettle();
    }
  });
  containerRO.observe(rootEl.value);
}

/*
 Высота прилипшей шапки, по ней ставим бегущую полоску загрузки.
*/

const headerHeight = ref<number>(0);
let theadRO: ResizeObserver | null = null;
function setupTheadObserver() {
  const table = bodyTableEl.value;
  if (!table) return;
  const thead = table.querySelector("thead") as HTMLElement | null;
  if (!thead) return;
  theadRO?.disconnect();
  theadRO = new ResizeObserver(() => { headerHeight.value = thead.offsetHeight; measureLayout(); });
  theadRO.observe(thead);
  headerHeight.value = thead.offsetHeight;
  measureLayout();
}

/* Отступ верхней границы <table> от верха контейнера .qtl-tables. Над таблицей
   внутри скролла лежит блок формул переменной высоты, а в режиме scroll шапка
   уезжает вместе с содержимым — поэтому положение таблицы считаем динамически
   через реальные прямоугольники, а не от верха скролл-контейнера. По нему
   позиционируем полоску загрузки (низ шапки = offset + высота шапки) и оверлей
   ресайза (верх таблицы), чтобы они не съезжали и не залезали в зону формул. */
const tableTopOffset = ref<number>(0);
/* Отступ полоски загрузки справа: держим её правый край по правому краю шапки (= правому
   краю таблицы), но не дальше видимой области контента скролла. Иначе при жёсткой ширине
   таблицы полоска торчит правее шапки и наезжает на вертикальный скроллбар. */
const stripRight = ref<number>(0);
function measureLayout() {
  const table = bodyTableEl.value;
  const tables = tablesEl.value;
  if (!table || !tables) { tableTopOffset.value = 0; stripRight.value = 0; updateScrollMargin(); return; }
  const tablesRect = tables.getBoundingClientRect();
  tableTopOffset.value = table.getBoundingClientRect().top - tablesRect.top;
  const tableRight = table.getBoundingClientRect().right - tablesRect.left;
  let rightEdge = tableRight;
  const scroll = bodyScrollEl.value;
  if (scroll) {
    // правая граница контента скролла (без вертикального скроллбара) в координатах .qtl-tables
    const contentRight = scroll.getBoundingClientRect().left + scroll.clientWidth - tablesRect.left;
    rightEdge = Math.min(tableRight, contentRight);
  }
  stripRight.value = Math.max(0, Math.round(tablesRect.width - rightEdge));
  updateScrollMargin();
}

/* Скролл тела таблицы двигает таблицу относительно контейнера, поэтому при прокрутке
   пересчитываем отступ. Слушатель вешаем на актуальный скролл-элемент. */
let layoutScrollEl: HTMLElement | null = null;
const onLayoutScroll = () => measureLayout();
watch(bodyScrollEl, (el) => {
  if (layoutScrollEl) layoutScrollEl.removeEventListener("scroll", onLayoutScroll);
  layoutScrollEl = el;
  if (el) el.addEventListener("scroll", onLayoutScroll, { passive: true });
  nextTick(() => measureLayout());
}, { immediate: true });

/* Блок формул меняет высоту вместе с данными модели — пересчитываем после перерисовки. */
watch(formulaResults, () => { nextTick(() => measureLayout()); });

/* ---------------------------------------------------------------------------
   Виртуализация строк (padding-rows на @tanstack/vue-virtual).

   В DOM держим только видимое окно строк + распорки сверху/снизу. Ширины колонок
   к этому моменту уже зафиксированы (fixed-layout), поэтому окно строк не влияет
   на ширины — горизонтально ничего не скачет. Высоты строк динамические: их
   замеряет виртуализатор, кеш замеров стабилен по entityId.
--------------------------------------------------------------------------- */

// 0 = виртуализировать всегда. Порог можно поднять, чтобы маленькие таблицы
// рисовались целиком (тогда в QTLiteBody работает плоский путь рендера).
const VIRTUALIZE_THRESHOLD = 0;

// В режиме page у таблицы нет своей прокрутки (overflow: visible), там
// виртуализировать нечем — рисуем все строки как раньше.
const virtualized = computed<boolean>(() =>
  headerMode.value !== "page" && rows.value.length > VIRTUALIZE_THRESHOLD,
);

// Оценка высоты ещё не измеренных строк. Снимаем медиану с реально отрисованных
// строк (в фазе сэмпла их MEASURE_SAMPLE) и держим стабильной, чтобы суммарная
// высота и скроллбар не «дышали» при прокрутке.
const estimatedRowSize = ref<number>(44);
function captureRowSizeEstimate() {
  const table = bodyTableEl.value;
  if (!table) return;
  const trs = table.querySelectorAll<HTMLElement>("tbody tr[data-entity-id]");
  if (!trs.length) return;
  const sizes: number[] = [];
  trs.forEach((tr) => { const h = tr.offsetHeight; if (h > 0) sizes.push(h); });
  if (!sizes.length) return;
  sizes.sort((a, b) => a - b);
  estimatedRowSize.value = sizes[Math.floor(sizes.length / 2)] || estimatedRowSize.value;
}

// Отступ начала списка (tbody) от верха контента скролла: над строками в нормальном
// потоке лежат блок формул и шапка. Нужен виртуализатору, чтобы правильно считать
// видимое окно (инвариантен к самой прокрутке).
const scrollMargin = ref<number>(0);
function updateScrollMargin() {
  const scroll = bodyScrollEl.value;
  const table = bodyTableEl.value;
  if (!scroll || !table) { scrollMargin.value = 0; return; }
  const thead = table.querySelector("thead") as HTMLElement | null;
  const theadH = thead ? thead.offsetHeight : 0;
  const tableTopInContent =
    table.getBoundingClientRect().top - scroll.getBoundingClientRect().top + scroll.scrollTop;
  scrollMargin.value = Math.max(0, Math.round(tableTopInContent + theadH));
}

const rowVirtualizer = useVirtualizer(computed(() => ({
  count: rows.value.length,
  getScrollElement: () => bodyScrollEl.value,
  estimateSize: () => estimatedRowSize.value,
  overscan: 8,
  getItemKey: (index: number) => rows.value[index]?.entityId ?? index,
  scrollMargin: scrollMargin.value,
})));

const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems());
const totalSize = computed<number>(() => rowVirtualizer.value.getTotalSize());

// Батч-замер высот строк (как в дереве слева): копим элементы и меряем пачками на
// rAF, чтобы не читать layout по одной строке за раз. measureElement заодно вешает
// на строку наблюдатель размера, поэтому позднее изменение высоты (выравнивание
// параметров) подхватывается само.
let measureQueue: HTMLElement[] = [];
let measureRaf: number | null = null;
const MEASURE_BATCH = 8;
function drainMeasureQueue() {
  measureRaf = null;
  const chunk = measureQueue.splice(0, MEASURE_BATCH);
  for (const el of chunk) {
    try { rowVirtualizer.value.measureElement(el); } catch { /* строка могла отмонтироваться */ }
  }
  if (measureQueue.length) measureRaf = requestAnimationFrame(drainMeasureQueue);
}
function measureRowElement(el: HTMLElement | null) {
  if (!el) return;
  if (!measureQueue.includes(el)) measureQueue.push(el);
  if (measureRaf === null) measureRaf = requestAnimationFrame(drainMeasureQueue);
}

const hasHeaderSnapshot = computed<boolean>(() => columns.value.length > 0 && headerHeight.value > 0);

// Полоску обновления и серый оверлей показываем только когда таблица уже была
// отрисована раньше — то есть это рефреш/дозагрузка той же таблицы. На первой
// отрисовке (initial, а также любой fresh-маунт при смене элемента — там родитель
// делает clearTable, и компонент монтируется заново с этим флагом = false) полоску
// не показываем: данные просто появляются под общим спиннером.
const firstLoadDone = ref<boolean>(false);
const showRefreshStrip = computed<boolean>(() => (
  loading.value && hasHeaderSnapshot.value && firstLoadDone.value
));

/* При появлении полоски загрузки пересчитываем её правый край: ширина шапки к этому
   моменту уже могла измениться (селектор ширины, ручной ресайз). */
watch(showRefreshStrip, () => { nextTick(() => measureLayout()); });

/*
 Выбор записей чекбоксами для массовой правки.
*/

const multi = useMultiEditState({
  selectedEverything: computed(() => props.selectedEverything || "none") as Ref<string>,
  selectedEntities: computed(() => props.selectedEntities || []) as Ref<number[]>,
  unselectedEntities: computed(() => props.unselectedEntities || []) as Ref<number[]>,
  emitSelected: (val) => emit("update:selectedEntities", val),
  emitUnselected: (val) => emit("update:unselectedEntities", val),
  emitSelectedAll: (val) => emit("update:selectedEverything", val),
  emitSelectionChangeManual: () => emit("selectionChangeManual"),
});

const headerCheckbox = computed<string>(() => multi.headerCheckbox.value);
const entityChecked = (id: number) => multi.entityChecked(id);

function onHeaderCheckboxChange(val: string) {
  multi.setHeaderCheckbox(val === "all" ? "all" : "none");
}

function onRowToggleCheckbox({ entityId, checked }: { entityId: number; checked: boolean }) {
  multi.updateSelection(checked, entityId);
}

function onCellUpdateSelection({ entityId, checked }: { entityId: number; checked: boolean }) {
  multi.updateSelection(checked, entityId);
}

function onCheckboxClick() {
  emit("selectionChangeManual");
}

function onMultieditClick(ev: MouseEvent) {
  emit("multieditClick", ev);
}

function onTogglePinnedOrder() {
  emit("togglePinnedOrder");
}

/*
 Ширины колонок.

 В userWidths лежат ширины только тех колонок, которые пользователь сам растянул,
 которые мы подогнали под новые данные при обновлении, или которые пришли
 сохранёнными. Остальные раскидывает браузер.
*/

const userWidths = reactive<Record<string, number>>({});

/* Пользователь сам зафиксировал ширины: либо потянул границу колонки, либо к
   таблице применились его ранее сохранённые ширины. Пока этого не было, ширины
   считаются авто (их задаёт селектор + раскладка браузера) и должны сами
   подстраиваться под изменение размера контейнера. Ширины, временно замороженные
   на время дозагрузки ради стабильности шапки, этот флаг НЕ поднимают — они не
   являются выбором пользователя. */
const userResized = ref<boolean>(false);

/** запомненные ширины всех колонок на время обновления, чтобы шапка не дёргалась */
const frozenColWidths = ref<Record<string, number> | null>(null);

/* Живые ширины двух колонок во время ручного таскания границы при виртуализации.
   Перекрывают userWidths/frozenColWidths в colStyle. Нужны потому, что при
   виртуализации перенос текста в ячейке меняет высоту строки, виртуализатор
   пересчитывает окно и провоцирует ре-рендер тела; без реактивной ширины ре-рендер
   возвращал бы <col> к исходной ширине (виден прыжок к старому размеру), затирая
   ту, что мы выставили в DOM императивно. */
const dragLiveWidths = reactive<Record<string, number>>({});

/** запомненное значение переключателя ширины на время обновления, чтобы при обычной
    дозагрузке он не сбрасывался к тому, что пришло с данными */
let frozenTableWidth: number | null = null;

/** структура шапки на момент старта обновления; если к приходу данных она не
    изменилась — ширины возвращаем, если изменилась — пересчитываем как при первой
    загрузке */
let frozenStructureKey: string | null = null;

/** идёт обычная дозагрузка (не самая первая): на это время держим ширины и
    переключатель ширины, не давая им сброситься от пришедших данных */
let reloadActive = false;

/** ниже этого колонку точно не ужимаем, а нормальный минимум обеспечит сам браузер */
const MIN_RESIZE_COL_WIDTH = 24;
const MULTIEDIT_FIXED_WIDTH = 55;
/* Левый и правый бордер рамки самой таблицы (по 1px, см. .qtl-table--body в QTLiteBody).
   При box-sizing: border-box (его глобально ставит quasar) ширина таблицы включает рамку,
   поэтому в жёстком режиме к сумме ширин колонок добавляем рамку — иначе она «съедает»
   эти пиксели у колонок и после ресайза таблица ужимается. */
const TABLE_BORDER_X = 2;

/*
 Когда у всех колонок есть запомненная ширина (пользователь растянул, мы подогнали
 при обновлении или ширины пришли сохранёнными), переводим таблицу в режим жёстких
 ширин. Тогда браузер держит ровно эти ширины и ничего не пересчитывает сам, поэтому
 после ресайза и после обновления ничего не скачет. Колонка с чекбоксами всегда
 известна, у неё ширина жёсткая.
*/
const widthsLocked = computed<boolean>(() => {
  const cols = columns.value;
  if (!cols.length) return false;
  const frozen = frozenColWidths.value;
  for (const c of cols) {
    if (c.isMultiedit) continue;
    const w = frozen ? frozen[c.id] : userWidths[c.id];
    if (!(w > 0)) return false;
  }
  return true;
});

const lockedTableWidthPx = computed<number>(() => {
  const cols = columns.value;
  const frozen = frozenColWidths.value;
  let s = 0;
  for (const c of cols) {
    if (c.isMultiedit) { s += MULTIEDIT_FIXED_WIDTH; continue; }
    const w = frozen ? frozen[c.id] : userWidths[c.id];
    if (w > 0) s += w;
  }
  // добавляем рамку таблицы, чтобы при border-box она не ужимала колонки (см. TABLE_BORDER_X)
  return s > 0 ? s + TABLE_BORDER_X : 0;
});

/*
 Двухфазный подсчёт ширин без дорогой раскладки всей таблицы.

 Раскладка table-layout:auto с width:min-content стоит пропорционально числу строк,
 а при сотнях строк с параметрами это секунды на каждый пересчёт (окно, селектор
 ширины, первая отрисовка). Поэтому всю таблицу в авто-раскладке НИКОГДА не держим:
   1. пока ширины не зафиксированы, рисуем только первые MEASURE_SAMPLE строк в
      авто-раскладке — по ним браузер дёшево раскидывает колонки;
   2. снимаем получившиеся ширины и фиксируем их (table-layout:fixed) на всю таблицу;
   3. дальше при ресайзе окна и смене селектора ширины только масштабируем эти ширины,
      не пересчитывая раскладку по всем строкам.
*/
const MEASURE_SAMPLE = 40;

/* Сколько строк реально рисовать: пока ширины не зафиксированы — только сэмпл для
   замера, иначе все. Отдаём в QTLiteBody, больше нигде число строк в DOM не решается. */
const measureSampleSize = computed<number | null>(() => (widthsLocked.value ? null : MEASURE_SAMPLE));

/* Снимаем ширины колонок с уже отрисованной таблицы (в фазе замера это сэмпл) и
   кладём в userWidths. userResized НЕ поднимаем: это авто-фиксация, при смене колонок
   или селектора ширины она честно сбросится и пересчитается. */
function lockWidthsFromDom(): boolean {
  const w = readAllColWidths();
  const ids = columnIds.value;
  let any = false;
  for (const id of ids) {
    if (w[id] > 0) { userWidths[id] = w[id]; any = true; }
  }
  return any;
}

/* После того как отрисовался сэмпл, снимаем с него ширины и фиксируем таблицу.
   Откладываем на кадр, чтобы браузер успел разложить сэмпл. Идемпотентно на кадр. */
let lockFromSampleScheduled = false;
function scheduleLockFromSample() {
  if (lockFromSampleScheduled) return;
  lockFromSampleScheduled = true;
  nextTick(() => requestAnimationFrame(() => {
    lockFromSampleScheduled = false;
    if (reloadActive) return;        // ширины держит логика дозагрузки
    if (userResized.value) return;   // пользователь/сохранённые ширины уже заданы
    if (widthsLocked.value) return;  // уже зафиксировано другим путём
    if (!columns.value.length) return;
    lockWidthsFromDom();
    // сэмпл ещё в DOM — снимаем с него медианную высоту строки для оценки
    // виртуализатора, пока не переключились на fixed-окно
    captureRowSizeEstimate();
  }));
}

/* Как только таблица оказалась без фиксированных ширин (например, после дозагрузки со
   сменой структуры), запускаем замер по сэмплу и фиксируем. */
watch(widthsLocked, (locked) => {
  if (!locked) scheduleLockFromSample();
}, { flush: "post" });

/* Контейнер сменил ширину (окно, левая панель, девтулс). Во время перетаскивания ширины
   НЕ трогаем: таблица держит фиксированную ширину, и браузер не перекладывает все 800
   строк на каждый кадр (это стоило бы ~секунды за кадр). Когда ресайз замер, сбрасываем
   ширины, чтобы колонки заново разложились под новый размер контейнера. Это происходит
   ВСЕГДА, в том числе после ручного растягивания столбца: ручные ширины держатся только
   пока контейнер не меняли (смена страницы и т.п.). Сброс делает widthsLocked=false, и
   его watcher запускает замер по сэмплу (40 строк, дёшево) с re-lock под новую ширину —
   в авто-раскладке таблица занимает min-width: множитель*100% контейнера, то есть
   колонки подстраиваются под новый размер. */
function readaptWidthsAfterResize() {
  if (reloadActive) return;
  for (const k of Object.keys(userWidths)) delete userWidths[k];
  frozenColWidths.value = null;
  userResized.value = false;
}

/* метка, по смене которой строки заново выравнивают высоты параметров. Меняется
   только при смене набора колонок и после того, как отпустили границу колонки.
   Ширину окна и переключатель ширины сюда не кладём: при их изменении строки и так
   перерисовываются, и слежение за размером внутри строки само подравняет высоты,
   а лишний общий пересчёт по всем строкам тормозил при каждом переключении. */
const rowHeightAlignStamp = computed<string>(() => {
  const ids = columnIds.value;
  return `${ids.join("|")}::${resizeSettleCounter.value}`;
});

const bodyContentMode = computed<"normal" | "dim" | "hide">(() => (
  showRefreshStrip.value ? refreshBodyMode.value : "normal"
));
/** как показывать строки во время обновления: бледными (по умолчанию) или спрятать */
const refreshBodyMode = ref<"dim" | "hide">("dim");

/* Читаем нынешние ширины всех колонок прямо со страницы. */
function readAllColWidths(): Record<string, number> {
  const out: Record<string, number> = {};
  const table = bodyTableEl.value;
  if (!table) return out;
  const ths = table.querySelectorAll<HTMLElement>("thead th[data-col-id]");
  ths.forEach((th) => {
    const id = th.dataset.colId;
    if (id) out[id] = th.getBoundingClientRect().width;
  });
  return out;
}

/* Подставляем сохранённые ширины колонок, когда меняется набор колонок. */
function applyStoredWidths() {
  const ids = columnIds.value;
  const stored = qstore.getCurrentTableWidths;
  if (!stored || !stored.size) return;
  let applied = false;
  for (let i = 0; i < ids.length; i++) {
    const w = stored.get(i);
    if (typeof w === "number" && w > 0) { userWidths[ids[i]] = w; applied = true; }
  }
  // применились ранее сохранённые ширины пользователя — считаем их заданными вручную
  if (applied) userResized.value = true;
}

watch(() => columnsFingerprint.value, () => { applyStoredWidths(); }, { immediate: true });

/*
 Тянем правую границу колонки. Ширины во время таскания пишем прямо в DOM, минуя
 перерисовку Vue, чтобы не тормозило. Раскладку при этом оставляем браузеру
 (table-layout auto): он сам не даёт ужать колонку уже её содержимого, и никаких
 замеров минимальной ширины руками не нужно. Двигаются только две колонки, та что
 тянем и смежная справа, остальные стоят, потому что у всех на время таскания
 проставлены их текущие ширины. Когда отпускаем, читаем со страницы настоящие
 ширины (браузер мог не дать ужать колонку уже содержимого) и запоминаем именно их.
 При виртуализации те же две ширины дублируем в реактивную карту (dragLiveWidths),
 иначе ре-рендер тела вернул бы колонку к исходной ширине.
*/

let dragStartWidths: Record<string, number> = {};
let dragWidths: Record<string, number> = {};
let dragColEls: Record<string, HTMLElement> = {};
let dragThEls: Record<string, HTMLElement> = {};
let dragTable: HTMLTableElement | null = null;
// двигали ли мы вообще границу за это нажатие. Если просто кликнули и отпустили,
// ничего не меняем, иначе от повторных кликов копилась бы погрешность замеров
let dragMoved = false;
// успели ли подготовить ширины. Подготовку делаем не при нажатии, а при первом
// реальном движении мыши, чтобы простое нажатие на границу вообще ничего не двигало
let dragSetupDone = false;
// чем были стили таблицы и колонок до начала, чтобы вернуть всё как было при клике без перетаскивания
let dragRestoreTable: { tableLayout: string; width: string; minWidth: string } | null = null;
const dragRestoreColWidth: Record<string, string> = {};

function sumDragWidths(): number {
  let s = 0;
  for (const id in dragWidths) s += dragWidths[id];
  return s;
}

function onResizeStart() {
  const table = bodyTableEl.value;
  if (!table) return;
  beginColumnResize();
  dragTable = table;
  dragMoved = false;
  dragSetupDone = false;
  // в DOM здесь специально ничего не трогаем. Если просто нажать на границу и
  // отпустить, не двигая мышь, ни одна колонка не должна сдвинуться даже на пиксель.
  // Вся подготовка ширин и переход на раскладку браузера происходят в ensureDragSetup
  // при первом же движении мыши.
}

/* Готовим ширины к перетаскиванию. Срабатывает один раз, при первом движении мыши. */
function ensureDragSetup(colId: string, neighborId?: string) {
  if (dragSetupDone) return;
  const table = dragTable;
  if (!table) return;
  dragSetupDone = true;
  dragWidths = {};
  dragColEls = {};
  dragThEls = {};
  for (const k of Object.keys(dragRestoreColWidth)) delete dragRestoreColWidth[k];
  dragRestoreTable = {
    tableLayout: table.style.tableLayout,
    width: table.style.width,
    minWidth: table.style.minWidth,
  };
  // столбец с чекбоксами (если он вообще есть по правам) не трогаем: у него жёсткая
  // ширина и нет ручки ресайза. если его перемерять и проставлять заново, он прыгает на
  // пару пикселей из-за схлопнутых бордюров. когда его нет, первый столбец обычный и
  // считается по общим правилам
  const multieditId = columns.value.find((c) => c.isMultiedit)?.id ?? "";
  table.querySelectorAll<HTMLElement>("thead th[data-col-id]").forEach((th) => {
    const id = th.dataset.colId;
    if (!id) return;
    dragThEls[id] = th;
    if (id === multieditId) {
      dragWidths[id] = MULTIEDIT_FIXED_WIDTH;
      return;
    }
    // берём настоящую дробную ширину, без округления, иначе при нажатии все колонки
    // чуть сдвинутся (микропрыжок). Округляем уже только когда сохраняем в конце.
    dragWidths[id] = th.getBoundingClientRect().width;
  });
  table.querySelectorAll<HTMLElement>("col[data-col-id]").forEach((c) => {
    const id = c.dataset.colId;
    if (!id) return;
    dragColEls[id] = c;
    dragRestoreColWidth[id] = c.style.width;
    if (id === multieditId) return;
    if (dragWidths[id] > 0) c.style.width = `${dragWidths[id]}px`;
  });
  dragStartWidths = { ...dragWidths };
  // Все колонки, кроме двух у границы, закрепляем жёстко: ставим им минимальную ширину
  // равной нынешней, чтобы они не двигались. Таблицу держим ровно той же ширины (как
  // задал селектор), значит место можно перераспределять только между этими двумя
  // колонками. Раскладку при этом считает браузер, поэтому он сам не даст ужать колонку
  // уже содержимого, и когда соседняя упрётся, тянущаяся просто остановится.
  for (const id in dragThEls) {
    if (id === colId || id === neighborId) dragThEls[id].style.removeProperty("min-width");
    else if (id === multieditId) dragThEls[id].style.minWidth = `${MULTIEDIT_FIXED_WIDTH}px`;
    else dragThEls[id].style.minWidth = `${dragWidths[id]}px`;
  }
  table.style.tableLayout = "auto";
  // сообщаем телу таблицы, что таблица теперь на auto-раскладке: пока таскаем, tableStyle
  // не должен возвращать её в fixed на ре-рендерах виртуализатора (иначе текст обрежется)
  isColumnDragLayoutAuto.value = true;
  // ширину таблицы берём настоящую, прямо со страницы, а не как сумму колонок: иначе
  // из-за схлопнутых бордюров столбца с чекбоксами сумма выходит на пару пикселей
  // меньше, и справа вылезает лишний отступ
  table.style.width = `${table.getBoundingClientRect().width}px`;
  table.style.minWidth = "0px";
}

function softMinOf(colId: string): number {
  // мягкий нижний предел, только чтобы не уйти в ноль; настоящий предел по
  // содержимому обеспечивает сам браузер. у столбца с чекбоксами он жёсткий
  const col = columns.value.find((c) => c.id === colId);
  return col?.isMultiedit ? MULTIEDIT_FIXED_WIDTH : MIN_RESIZE_COL_WIDTH;
}

function onResize({ colId, neighborId, delta }: { colId: string; neighborId?: string; delta: number }) {
  if (!dragTable) return;
  // первое движение мыши: только сейчас замеряем ширины и переводим таблицу на
  // раскладку браузера. До этого момента нажатие на границу ничего не двигало.
  ensureDragSetup(colId, neighborId);
  const colEl = dragColEls[colId];
  if (!colEl) return;
  const startCol = dragStartWidths[colId] || 0;

  // Считаем целевую ширину от исходной плюс сдвиг курсора. Место берём только у
  // смежной колонки: одна растёт ровно на столько, на сколько другая ужимается, общая
  // ширина пары постоянна. Ширину таблицы и остальные колонки не трогаем, поэтому
  // ничего больше не двигается. Если соседнюю нельзя ужать уже содержимого, браузер
  // не даст, тянущаяся остановится, а при движении назад тронется только когда курсор
  // дойдёт до настоящей границы.
  if (neighborId && dragColEls[neighborId]) {
    const startNb = dragStartWidths[neighborId] || 0;
    const pair = startCol + startNb;
    let targetCol = startCol + delta;
    if (targetCol < softMinOf(colId)) targetCol = softMinOf(colId);
    if (pair - targetCol < softMinOf(neighborId)) targetCol = pair - softMinOf(neighborId);
    const targetNb = pair - targetCol;
    colEl.style.width = `${targetCol}px`;
    dragColEls[neighborId].style.width = `${targetNb}px`;
    // при виртуализации дублируем ширины в реактивную карту, чтобы ре-рендер тела
    // (его провоцирует замер высот виртуализатором при переносе текста) не вернул
    // <col> к исходной ширине
    if (virtualized.value) {
      dragLiveWidths[colId] = targetCol;
      dragLiveWidths[neighborId] = targetNb;
    }
    dragMoved = true;
    return;
  }

  // Соседа справа нет: просто меняем эту, таблица становится шире или уже.
  const targetCol = Math.max(softMinOf(colId), startCol + delta);
  colEl.style.width = `${targetCol}px`;
  dragWidths[colId] = targetCol;
  dragTable.style.width = `${sumDragWidths()}px`;
  if (virtualized.value) dragLiveWidths[colId] = targetCol;
  dragMoved = true;
}

function restoreTableProp(el: HTMLElement, prop: "tableLayout" | "width" | "minWidth", value: string) {
  if (value) el.style[prop] = value;
  else el.style.removeProperty(prop === "tableLayout" ? "table-layout" : prop === "minWidth" ? "min-width" : "width");
}

function onResizeEnd() {
  // Просто кликнули по границе и отпустили, не двигая: подготовку мы так и не делали,
  // в DOM ничего не трогали, значит и возвращать нечего. Просто выходим.
  if (!dragSetupDone || !dragMoved) {
    isColumnDragLayoutAuto.value = false;
    for (const k of Object.keys(dragLiveWidths)) delete dragLiveWidths[k];
    for (const id in dragThEls) dragThEls[id].style.removeProperty("min-width");
    for (const id in dragColEls) {
      const prev = dragRestoreColWidth[id];
      if (prev) dragColEls[id].style.width = prev;
      else dragColEls[id].style.removeProperty("width");
    }
    if (dragTable && dragRestoreTable) {
      restoreTableProp(dragTable, "tableLayout", dragRestoreTable.tableLayout);
      restoreTableProp(dragTable, "width", dragRestoreTable.width);
      restoreTableProp(dragTable, "minWidth", dragRestoreTable.minWidth);
    }
    dragTable = null;
    dragWidths = {};
    dragStartWidths = {};
    dragColEls = {};
    dragThEls = {};
    dragRestoreTable = null;
    dragSetupDone = false;
    endColumnResize();
    return;
  }

  const ids = columnIds.value;
  // браузер мог не дать ужать колонку уже содержимого, поэтому берём настоящие
  // ширины со страницы и запоминаем именно их
  const real = readAllColWidths();
  for (const id of ids) {
    const w = real[id] || dragWidths[id] || 0;
    if (w <= 0) continue;
    userWidths[id] = w;
    const idx = ids.indexOf(id);
    if (idx >= 0) qstore.setColumnWidth(idx, w);
  }
  // пользователь сам задал ширины — дальше под контейнер их не пересчитываем
  userResized.value = true;
  // ширины закоммичены в userWidths — снимаем реактивный оверрайд таскания, чтобы
  // colStyle взял их из userWidths (значения совпадают, прыжка нет), и возвращаем
  // таблицу в fixed
  isColumnDragLayoutAuto.value = false;
  for (const k of Object.keys(dragLiveWidths)) delete dragLiveWidths[k];
  // снимаем закрепление соседних колонок и временные стили таблицы, дальше ширины
  // держит userWidths (widthsLocked)
  for (const id in dragThEls) dragThEls[id].style.removeProperty("min-width");
  if (dragTable) {
    dragTable.style.removeProperty("table-layout");
    dragTable.style.removeProperty("width");
    dragTable.style.removeProperty("min-width");
  }
  dragTable = null;
  dragWidths = {};
  dragStartWidths = {};
  dragColEls = {};
  dragThEls = {};
  dragSetupDone = false;
  endColumnResize();
}

/*
 Сортировка.
*/

function onSort({ col, nextOrder }: { col: LiteColumn; nextOrder: number }) {
  const p = props.paginationModel;
  if (!p) return;
  // Третий клик (nextOrder === 1) сбрасывает сортировку. Запрос остаётся тем же
  // (/Ordered, OrderMode как обычно), но ColumnCategory шлём null — тогда сортировка
  // по столбцу сбрасывается. Маршрут на /Initial тут не нужен.
  const sortCategory = nextOrder === 1 ? null : col.sortCategory;
  if (!props.isSector) {
    p.recalculate = false;
    p.forceInitial = true;
    p.offset = 0;
    p.sortCategory = sortCategory;
    p.sortMode = nextOrder;
    p.OrderingEntityIds = col.sortEntityIds || [];
    emit("update:paginationModel", p);
    qstore.getResultTable(props.srcElement, p, p.report, p.report_index);
  } else {
    p.sortCategory = sortCategory;
    p.sortMode = nextOrder;
    p.OrderingEntityIds = col.sortEntityIds || [];
    emit("update:paginationModel", p);
    emit("refreshPage", p);
  }
}

/*
 События, которые прилетают из ячеек, как и в обычной таблице.
*/

function onRecordHover(_p: any) {}
function onChangeHeights(_p: any) {}
function onRefreshNeeded() { emit("refreshNeeded"); }
function onUpdatePerson(p: { ev: MouseEvent; entityId: number }) { emit("updatePerson", p); }
const sourceDialogRef = ref<InstanceType<typeof SourceItemsDialog> | null>(null);
function onSourceItemsRequested(p: { ids: number[]; type: number; entityId: number }) {
  sourceDialogRef.value?.open(p.ids, p.type);
}

/*
 Слежение за изменениями и жизненный цикл.
*/

/* Начальный номер строк (offset) берём из УЖЕ отрисованных данных, а не из текущей
   пагинации. Иначе при переходе на другую страницу offset меняется сразу по клику, и
   нумерация обновлялась бы раньше, чем прилетит и отрисуется новый контент. Обновляем
   его в том же колбэке, где перестраиваются строки, — тогда номера и контент меняются
   одновременно. */
const displayedRowNumberBase = ref<number>(props.paginationModel?.offset || 0);

watch(
  () => props.modelValue,
  () => {
    tableModel.rebuildRows();
    displayedRowNumberBase.value = props.paginationModel?.offset || 0;
  },
  { deep: false },
);

/*
  Переход по страницам: данные подменяются на месте (контейнер прокрутки тот же),
  поэтому браузер сохраняет вертикальную позицию от прошлой страницы. Прокрутку в
  начало делаем НЕ в момент смены offset (начало запроса) — там таблица заблокирована
  и держит позицию под полосой загрузки, — а только после того как новая страница
  догрузилась (см. watch(loading) ниже). Признак страницы — offset выборки (при
  сортировке он тоже становится 0, прокрутку в начало там тоже ждём). Горизонтальную
  прокрутку (какие колонки видны) сознательно не трогаем.
*/
let pendingPageScrollReset = false;
watch(
  () => props.paginationModel?.offset,
  (next, prev) => {
    if (next === prev) return;
    pendingPageScrollReset = true;
  },
);

/*
  Пользователь сам поменял переключатель ширины: применяем сразу, как было раньше.
  Сбрасываем заданные пользователем и запомненные ширины, чтобы браузер заново
  разложил колонки под новую ширину.

  Во время обычной дозагрузки переключатель меняем не мы, а пришедшие с сервера
  данные (там ширина бывает другой). В этом случае ничего не сбрасываем: ширины
  держим, а сам переключатель вернём как был после загрузки.
*/
watch(
  () => effectiveMultiplier.value,
  (nv, ov) => {
    if (reloadActive) return;
    if (!(ov > 0) || !(nv > 0) || nv === ov) return;
    // Выбрали новый пресет общей ширины: сбрасываем заданные/запомненные ширины, чтобы
    // колонки заново разложились под новый множитель. Это работает всегда, в том числе
    // после ручного растягивания колонок. Сброс делает widthsLocked=false, а его watcher
    // запускает замер по сэмплу (40 строк, дёшево) и снова фиксирует ширины под новый
    // множитель — то есть быстро и корректно, без раскладки всех строк.
    for (const k of Object.keys(userWidths)) delete userWidths[k];
    frozenColWidths.value = null;
    userResized.value = false;
  },
);

/*
  Поменялся набор колонок: сбрасываем все ширины и заново подставляем
  сохранённые, если они подходят к новому набору. И снова начинаем следить за
  высотой шапки, потому что её перерисовало.
*/
watch(
  () => columnsFingerprint.value,
  async () => {
    for (const k of Object.keys(userWidths)) delete userWidths[k];
    frozenColWidths.value = null;
    // новый набор колонок: ширины снова авто, applyStoredWidths поднимет флаг обратно,
    // если к этому набору подошли сохранённые пользователем ширины
    userResized.value = false;
    applyStoredWidths();
    await nextTick();
    setupTheadObserver();
    // новый набор колонок: если сохранённые ширины не подошли, замерим по сэмплу и
    // зафиксируем, чтобы всю таблицу не раскладывать в авто
    scheduleLockFromSample();
  },
);

/*
  Обновление таблицы: если это не самая первая загрузка, запоминаем текущие
  ширины колонок и держим их, чтобы шапка не дёргалась. Когда пришли новые данные,
  сравниваем ширины: если где-то данные не влезли и колонка стала шире, принимаем
  её новую ширину и сохраняем, как при ручном растягивании. Если всё влезло,
  ширины не трогаем.
*/
watch(loading, async (isLoading, was) => {
  // Первая загрузка этого инстанса завершилась — дальше любые загрузки уже рефреш
  // существующей таблицы, можно показывать полоску обновления.
  if (!isLoading && was) {
    firstLoadDone.value = true;
    // Новая страница догрузилась: теперь, когда блокировка прокрутки снята вместе с
    // полосой, прокручиваем таблицу в начало. На смену offset сразу не сбрасывали,
    // чтобы во время загрузки таблица держала прежнюю позицию и не дёргалась.
    if (pendingPageScrollReset) {
      pendingPageScrollReset = false;
      nextTick(() => {
        const el = bodyScrollEl.value;
        if (el) el.scrollTop = 0;
        // сбрасываем и виртуальное окно в начало, чтобы оно не осталось на позиции
        // прошлой страницы
        try { rowVirtualizer.value.scrollToOffset(0); } catch { /* виртуализатор мог быть не готов */ }
      });
    }
  }
  if (isLoading && !was) {
    // Замораживаем ширины на любую дозагрузку (включая recalc): решение о сохранении
    // принимаем по структуре уже после прихода данных. Запоминаем структуру шапки.
    if (!hasHeaderSnapshot.value) return;
    const snap = readAllColWidths();
    if (Object.keys(snap).length) {
      frozenColWidths.value = snap;
      frozenStructureKey = columnsStructureKey.value;
      // запоминаем и переключатель ширины, чтобы вернуть его, если данные принесут другой
      frozenTableWidth = props.modelValue?.TableWidth ?? null;
      reloadActive = true;
    }
  } else if (!isLoading && was && reloadActive) {
    await nextTick();
    // Структуру шапки сравниваем уже на новых данных (columns пересчитаны из нового
    // HeaderRow). Та же структура -> ширины сохраняем; другая -> пересчитываем заново.
    const sameStructure = frozenStructureKey === columnsStructureKey.value;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (sameStructure) {
        // запомненные на время обновления ширины оставляем как есть и переносим в
        // userWidths, чтобы колонки не разъехались обратно по содержимому новых данных.
        // То есть после обновления ширины остаются ровно теми же, ничего не скачет.
        const W0 = frozenColWidths.value || {};
        const ids = columnIds.value;
        for (const id of ids) {
          const w = W0[id] || 0;
          if (w > 0) userWidths[id] = w;
        }
        // возвращаем переключатель ширины как был до дозагрузки: на обычной дозагрузке
        // он не должен сбрасываться к тому, что пришло с данными. modelValue это один и
        // тот же объект из хранилища, на который смотрит и панель сверху, поэтому пишем
        // прямо в него: так значение сразу видно везде, без лишней пересборки строк.
        if (
          frozenTableWidth != null
          && props.modelValue
          && props.modelValue.TableWidth !== frozenTableWidth
        ) {
          // eslint-disable-next-line vue/no-mutating-props
          props.modelValue.TableWidth = frozenTableWidth;
        }
      } else {
        // структура шапки изменилась — ширины считаем заново, как при первой загрузке:
        // сбрасываем пользовательские ширины и подставляем сохранённые (серверные),
        // остальное раскладывает браузер по содержимому.
        for (const k of Object.keys(userWidths)) delete userWidths[k];
        applyStoredWidths();
      }
      frozenColWidths.value = null;
      frozenTableWidth = null;
      frozenStructureKey = null;
      resizeSettleCounter.value++;
      setupTheadObserver();
      // reloadActive снимаем на следующем тике: возврат переключателя выше тоже
      // дёрнет наблюдатель ширины, и пока reloadActive включён, он не сбросит колонки
      nextTick(() => { reloadActive = false; });
    }));
  }
});

const emptyMessage = computed(() => t("table_empty"));

onMounted(async () => {
  // Если к моменту маунта загрузка уже завершилась (данные пришли до монтирования),
  // считаем первую отрисовку состоявшейся, чтобы будущие рефреши показывали полоску.
  if (!loading.value) firstLoadDone.value = true;
  if (rootEl.value) containerWidth.value = rootEl.value.clientWidth;
  setupContainerObserver();
  tableModel.rebuildRows();
  displayedRowNumberBase.value = props.paginationModel?.offset || 0;
  await nextTick();
  requestAnimationFrame(() => {
    setupTheadObserver();
    measureLayout();
    // первая отрисовка идёт по сэмплу строк (measureSampleSize), тут снимаем ширины
    // и фиксируем таблицу, дальше всё живёт в fixed-layout
    scheduleLockFromSample();
  });
});

onBeforeUnmount(() => {
  // снимаем незавершённый settle ресайза окна и сбрасываем общий признак, чтобы он
  // не остался включённым и не заглушил пересчёт высот у другой таблицы
  if (windowResizeSettleTimer !== null) { clearTimeout(windowResizeSettleTimer); windowResizeSettleTimer = null; }
  if (isWindowResizing.value) isWindowResizing.value = false;
  if (measureRaf !== null) { cancelAnimationFrame(measureRaf); measureRaf = null; }
  measureQueue = [];
  containerRO?.disconnect();
  containerRO = null;
  theadRO?.disconnect();
  theadRO = null;
  if (layoutScrollEl) {
    layoutScrollEl.removeEventListener("scroll", onLayoutScroll);
    layoutScrollEl = null;
  }
});

/*
 То, что родитель может вызвать у таблицы при необходимости.
*/

defineExpose({
  /** сбросить заданные пользователем ширины и дать браузеру пересчитать всё заново */
  forceMeasure: async () => {
    for (const k of Object.keys(userWidths)) delete userWidths[k];
    frozenColWidths.value = null;
    userResized.value = false;
    await nextTick();
  },
  replaceRow: tableModel.replaceRow,
});
</script>

<style lang="scss" scoped>
.qtl-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  /* contain без size, иначе при высоте родителя по содержимому всё схлопнется в ноль */
  contain: layout style paint;
  min-height: 0;
}

/*
  Когда таблица сама по себе, а не в окне или секторе, у родителя нет заданной
  высоты, как было и раньше. Поэтому берём высоту от окна с поправкой на панели.
*/
.qtl-shell--standalone {
  /*
    Высоту задаёт родитель: внешняя обёртка (QueryTable.vue) измеряет свой верх и
    тянется ровно до низа окна с отступом, а table_container — flex-колонка с
    flex: 1 1 0. Поэтому здесь тоже flex: 1 1 0 (а НЕ auto): базис ноль означает,
    что размер шелла берётся только из распределения свободного места, и контент
    не может «раздуть» высоту по числу строк. Это и держит таблицу стабильной
    после перезагрузки (тело скроллится, шелл не растёт под все строки), без
    привязанного к окну потолка max-height. min-height: 0 обязателен, иначе
    flex-ребёнок не даёт телу скроллиться.
  */
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
}

/*
  Режим page: у таблицы нет своей прокрутки, она растёт по содержимому и
  прокручивается вместе со страницей в общем контейнере, как было раньше.
  Поэтому снимаем заданную высоту и не обрезаем содержимое.
*/
.qtl-shell--page {
  height: auto;
  min-height: 0;
  overflow: visible;
  contain: none;
}

.qtl-tables {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* relative нужен, чтобы поверх легли полоски растягивания и бегущая полоска загрузки */
  position: relative;
  /* overflow держим скрытым, чтобы оверлей ресайза и бегущая полоска не вылезали за
     пределы зоны таблицы. Рамку со скруглением сюда НЕ ставим: иначе в неё попал бы
     и блок формул, лежащий над таблицей. Рамка-карточка теперь на самой таблице
     (см. .qtl-table--body в QTLiteBody), а формулы остаются над рамкой. */
  overflow: hidden;
  /* фон не задаём: вокруг карточки таблицы просвечивает фон страницы (#F2F2F2) */
  background: transparent;
}

/* Бегущая полоска загрузки, лежит поверх под шапкой, отступ сверху задаём прямо в разметке. */
.qtl-refresh-strip {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: #e8efe9;
  overflow: hidden;
  z-index: 4;
  pointer-events: none;
}

.qtl-refresh-strip__bar {
  position: absolute;
  inset: 0 auto 0 0;
  width: 28%;
  background: #3F884F;
  will-change: transform;
  animation: qtl-refresh-strip-move 0.85s linear infinite;
}

@keyframes qtl-refresh-strip-move {
  from {
    transform: translateX(-120%);
  }
  to {
    transform: translateX(460%);
  }
}
</style>
